'use client';

import type { ComponentProps } from 'react';
import { useResource } from '@/lib/use-resource';
import { BacktestView } from '@/components/BacktestView';
import { AlphaVsRestBacktest } from '@/components/AlphaVsRestBacktest';

// Professional, Sharpe-first backtest page. Reads from /api/backtest-history
// (dated harness outputs merged by run date). Leads with the validated headline,
// then the model-improvement trend over time, year-by-year returns, Alpha-vs-Rest,
// and a detailed run-history table. The legacy factor-IC analytics sit behind a toggle.
type BacktestData = ComponentProps<typeof BacktestView>['data'];

type HistoryRow = {
  date: string;
  simpleSharpe?: number;
  cagr?: number;
  vol?: number;
  mean?: number;
  perYear?: Record<string, number>;
  loocvSharpe?: number;
  p50?: number;
  p25?: number;
  prodCell?: number;
};

const GOLD = '#c9a84c';
const TEAL = '#2dd4bf';
const f2 = (n?: number) => (typeof n === 'number' ? n.toFixed(2) : '—');
const pct = (n?: number, d = 1) => (typeof n === 'number' ? `${(n * 100).toFixed(d)}%` : '—');

// ── Hero: the headline numbers, Sharpe dominant ──────────────────────────
function Hero({ row }: { row: HistoryRow }) {
  const years = row.perYear ? Object.values(row.perYear) : [];
  const winRate = years.length ? years.filter((v) => v > 0).length / years.length : undefined;
  const supporting = [
    { label: 'Annual return (CAGR)', v: pct(row.cagr) },
    { label: 'Volatility', v: pct(row.vol) },
    { label: 'Out-of-sample Sharpe', v: f2(row.loocvSharpe) },
    { label: 'Positive years', v: winRate !== undefined ? `${Math.round(winRate * years.length)} / ${years.length}` : '—' },
  ];
  return (
    <div className="rounded-xl border border-[#c9a84c]/30 bg-gradient-to-br from-[#c9a84c]/[0.07] to-transparent px-6 py-5">
      <div className="flex flex-col md:flex-row md:items-stretch gap-6">
        <div className="flex flex-col justify-center min-w-[200px]">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#c9a84c] font-semibold">Sharpe ratio</div>
          <div className="text-[64px] font-bold tabular-nums leading-none text-[#c9a84c] mt-1">{f2(row.simpleSharpe)}</div>
          <div className="text-[11px] text-[#94a3b8] mt-1.5">survivorship-corrected · 12 vintages · 2014–2025</div>
          <div className="h-px w-16 bg-[#c9a84c]/50 mt-3" />
        </div>
        <div className="hidden md:block w-px bg-[#1e293b]" />
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-2 gap-x-8 gap-y-3 flex-1 content-center">
          {supporting.map((s) => (
            <div key={s.label}>
              <div className="text-[24px] font-bold tabular-nums leading-none text-white">{s.v}</div>
              <div className="text-[10px] text-[#94a3b8] mt-1 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[10px] text-[#64748b] mt-4 leading-relaxed border-t border-[#1e293b] pt-3">
        As of {row.date}. Sharpe = average annual return divided by its volatility — the higher the number, the more
        return earned per unit of risk.
        {typeof row.prodCell === 'number' && ` Production-cell out-of-sample Sharpe (k=5 conviction, max 15% per name): ${f2(row.prodCell)}.`}
      </div>
    </div>
  );
}

// ── Centerpiece: how the model has improved over time ────────────────────
function ImprovementTrend({ history }: { history: HistoryRow[] }) {
  const dates = history.map((r) => r.date);
  if (dates.length < 2) return null;

  const series = [
    { key: 'simpleSharpe' as const, label: 'Sharpe (headline)', color: GOLD },
    { key: 'loocvSharpe' as const, label: 'Out-of-sample (walk-forward)', color: TEAL },
  ];

  const allVals = history.flatMap((r) => series.map((s) => r[s.key]).filter((v): v is number => typeof v === 'number'));
  if (allVals.length === 0) return null;
  const dataMin = Math.min(...allVals);
  const dataMax = Math.max(...allVals);
  const yMin = Math.floor((dataMin - 0.1) * 10) / 10;
  const yMax = Math.ceil((dataMax + 0.1) * 10) / 10;

  const W = 760, H = 250, padL = 40, padR = 16, padT = 16, padB = 40;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const xAt = (i: number) => padL + (dates.length === 1 ? plotW / 2 : (i / (dates.length - 1)) * plotW);
  const yAt = (v: number) => padT + plotH * (1 - (v - yMin) / (yMax - yMin || 1));

  const gridLines = 4;
  const ticks = Array.from({ length: gridLines + 1 }, (_, i) => yMin + ((yMax - yMin) / gridLines) * i);
  const overhaulIdx = dates.indexOf('2026-05-14');

  return (
    <div className="rounded-xl border border-[#1e293b] bg-[#0d1117] p-5">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-[13px] font-semibold text-white uppercase tracking-wide">How the model has improved</h3>
        <div className="flex items-center gap-4">
          {series.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5 text-[10px] text-[#94a3b8]">
              <span className="inline-block w-3 h-[2px]" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      </div>
      <p className="text-[10px] text-[#64748b] mb-2">Risk-adjusted return at each re-validation run. Up and to the right is better.</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} y1={yAt(t)} x2={W - padR} y2={yAt(t)} stroke="#1e293b" strokeWidth={1} />
            <text x={padL - 6} y={yAt(t) + 3} fill="#64748b" fontSize={9} textAnchor="end" fontFamily="var(--font-mono)">
              {t.toFixed(1)}
            </text>
          </g>
        ))}
        {overhaulIdx >= 0 && (
          <g>
            <line x1={xAt(overhaulIdx)} y1={padT} x2={xAt(overhaulIdx)} y2={padT + plotH} stroke="#475569" strokeWidth={1} strokeDasharray="3 3" />
            <text x={xAt(overhaulIdx) + 4} y={padT + 10} fill="#94a3b8" fontSize={8}>composite overhaul</text>
          </g>
        )}
        {series.map((s) => {
          const pts = history
            .map((r, i) => ({ i, v: r[s.key] }))
            .filter((p): p is { i: number; v: number } => typeof p.v === 'number');
          if (pts.length === 0) return null;
          const path = pts.map((p, k) => `${k === 0 ? 'M' : 'L'} ${xAt(p.i)} ${yAt(p.v)}`).join(' ');
          return (
            <g key={s.key}>
              <path d={path} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {pts.map((p) => (
                <g key={p.i}>
                  <circle cx={xAt(p.i)} cy={yAt(p.v)} r={3} fill="#0d1117" stroke={s.color} strokeWidth={2} />
                  <text x={xAt(p.i)} y={yAt(p.v) - 8} fill={s.color} fontSize={9} textAnchor="middle" fontFamily="var(--font-mono)" fontWeight="bold">
                    {p.v.toFixed(2)}
                  </text>
                </g>
              ))}
            </g>
          );
        })}
        {dates.map((d, i) => (
          <text key={d} x={xAt(i)} y={H - 14} fill="#64748b" fontSize={8} textAnchor="middle">
            {d.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

// ── Year-by-year returns ─────────────────────────────────────────────────
function PerYearReturns({ perYear }: { perYear: Record<string, number> }) {
  const years = Object.keys(perYear).sort();
  if (years.length === 0) return null;
  const vals = years.map((y) => perYear[y]);
  const maxV = Math.max(...vals, 0);
  const minV = Math.min(...vals, 0);
  const range = maxV - minV || 1;
  const W = 760, H = 180, padTop = 18, padBottom = 22;
  const plotH = H - padTop - padBottom;
  const zeroY = padTop + plotH * (maxV / range);
  const colW = W / years.length;
  const barW = colW * 0.6;

  return (
    <div className="rounded-xl border border-[#1e293b] bg-[#0d1117] p-5">
      <h3 className="text-[13px] font-semibold text-white uppercase tracking-wide">Year-by-year return</h3>
      <p className="text-[10px] text-[#64748b] mb-2">Survivorship-corrected basket, forward 12-month return per vintage.</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        <line x1={0} y1={zeroY} x2={W} y2={zeroY} stroke="#334155" strokeWidth={1} />
        {years.map((y, i) => {
          const v = perYear[y];
          const x = i * colW + (colW - barW) / 2;
          const h = (Math.abs(v) / range) * plotH;
          const yTop = v >= 0 ? zeroY - h : zeroY;
          const color = v >= 0 ? '#4ade80' : '#f87171';
          return (
            <g key={y}>
              <rect x={x} y={yTop} width={barW} height={h} fill={color} opacity={0.85} rx={1} />
              <text x={x + barW / 2} y={v >= 0 ? yTop - 3 : yTop + h + 10} fill={color} fontSize={9} textAnchor="middle" fontFamily="var(--font-mono)">
                {(v * 100).toFixed(0)}%
              </text>
              <text x={x + barW / 2} y={H - 6} fill="#64748b" fontSize={8} textAnchor="middle">{`'${y.slice(2)}`}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Detailed dated run history ────────────────────────────────────────────
function RunHistory({ history }: { history: HistoryRow[] }) {
  if (history.length === 0) return null;
  const latest = history[history.length - 1]?.date;
  return (
    <div className="rounded-xl border border-[#1e293b] bg-[#0d1117] p-5">
      <h3 className="text-[13px] font-semibold text-white uppercase tracking-wide mb-2">Run history</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] tabular-nums">
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-[#64748b] text-right">
              <th className="text-left py-1 pr-3 font-semibold">Run date</th>
              <th className="py-1 px-2 font-semibold">Sharpe</th>
              <th className="py-1 px-2 font-semibold">CAGR</th>
              <th className="py-1 px-2 font-semibold">Vol</th>
              <th className="py-1 px-2 font-semibold">Out-of-sample</th>
              <th className="py-1 px-2 font-semibold">Bootstrap p50</th>
              <th className="py-1 pl-2 font-semibold">Prod cell (k=5)</th>
            </tr>
          </thead>
          <tbody>
            {history.map((r) => {
              const isLatest = r.date === latest;
              return (
                <tr key={r.date} className={`text-right border-t border-[#1e293b]/60 ${isLatest ? 'text-[#c9a84c] font-semibold' : 'text-[#cbd5e1]'}`}>
                  <td className="text-left py-1 pr-3 font-mono">{r.date}</td>
                  <td className="py-1 px-2">{f2(r.simpleSharpe)}</td>
                  <td className="py-1 px-2">{pct(r.cagr)}</td>
                  <td className="py-1 px-2">{pct(r.vol)}</td>
                  <td className="py-1 px-2">{f2(r.loocvSharpe)}</td>
                  <td className="py-1 px-2">{f2(r.p50)}</td>
                  <td className="py-1 pl-2">{f2(r.prodCell)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[9px] text-[#64748b] mt-2 leading-relaxed">
        Sharpe = survivorship-corrected basket sim (headline). Out-of-sample = leave-one-out walk-forward, full
        methodology (tax/cost/turnover). Prod cell = k=5 conviction, max 15%. Blank = that test was not run that date.
      </div>
    </div>
  );
}

export function BacktestTab() {
  const { data: hist } = useResource<{ history: HistoryRow[] }>('/api/backtest-history');
  const { data: bt, error } = useResource<BacktestData>('/api/backtest');
  const history = hist?.history ?? [];
  const latest = history.length ? history[history.length - 1] : undefined;

  return (
    <div className="space-y-5">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-[18px] font-bold text-white tracking-tight">Backtest &amp; validation</h2>
          <p className="text-[11px] text-[#64748b]">12-vintage walk-forward, 2014–2025 · survivorship-corrected against Russell 3000 + S&amp;P/TSX Composite + ADR allowlist</p>
        </div>
      </div>

      <details className="rounded-xl border border-[#1e293b] bg-[#0d1117]">
        <summary className="cursor-pointer px-5 py-3 text-[12px] font-semibold text-[#94a3b8] hover:text-white">
          Methodology · how we test the model
        </summary>
        <div className="border-t border-[#1e293b] px-5 py-4 text-[12px] leading-relaxed text-[#cbd5e1]">
          <ul className="space-y-3 list-none m-0 p-0">
            <li>
              <span className="font-semibold text-white">No survivorship bias.</span>{' '}
              We rebuild the investable universe as it stood at the start of each year, 2014 to 2025
              (Russell 1000/3000, S&amp;P/TSX Composite, and a fixed ADR list), so companies that later
              failed or were delisted are still in the test. The model is judged against names the firm
              could actually have held, not only the winners visible in hindsight.
            </li>
            <li>
              <span className="font-semibold text-white">Out-of-sample, not curve-fit.</span>{' '}
              Every design choice is checked with leave-one-out cross-validation. We hide one year
              completely, set the model on the other years, then measure it on the year it never saw,
              and repeat for all twelve. The published numbers are the held-out years.
            </li>
            <li>
              <span className="font-semibold text-white">The settings earn their place.</span>{' '}
              Pillar weights, the Quality and Valuation blend, and basket size were each stress-tested
              this way. When an in-sample setting that looked best failed to repeat out-of-sample, the
              research-driven setting that did was kept.
            </li>
            <li>
              <span className="font-semibold text-white">What we report.</span>{' '}
              A monthly-rebalanced top-10 book, conviction-weighted, scored on the same Quality and
              Valuation factors shown across the platform. Returns are a disciplined simulation before
              trading costs and tax.
            </li>
            <li>
              <span className="font-semibold text-white">Honest about the trade-off.</span>{' '}
              The edge survives cross-validation, but it is concentration-dependent by design. A focused
              ten-name book carries more single-name risk than a broad index, which the firm manages
              deliberately rather than diversifying the edge away.
            </li>
          </ul>
        </div>
      </details>

      {latest ? <Hero row={latest} /> : <div className="text-[#94a3b8] text-sm py-4">Loading backtest summary…</div>}
      <ImprovementTrend history={history} />
      {latest?.perYear && <PerYearReturns perYear={latest.perYear} />}
      <AlphaVsRestBacktest />
      <RunHistory history={history} />

      <details className="rounded-xl border border-[#1e293b] bg-[#0d1117]">
        <summary className="cursor-pointer px-5 py-3 text-[12px] font-semibold text-[#94a3b8] hover:text-white">
          Factor detail — information coefficient, quintile spreads, factor heatmap (advanced)
        </summary>
        <div className="border-t border-[#1e293b]">
          {error ? (
            <div className="text-[#94a3b8] text-sm p-6">
              Factor backtest data unavailable. Generate it with <code className="text-amber-300">npm run backtest</code>.
            </div>
          ) : !bt ? (
            <div className="text-[#94a3b8] text-sm p-6">Loading factor detail…</div>
          ) : (
            <BacktestView data={bt} />
          )}
        </div>
      </details>
    </div>
  );
}
